import { Controller } from "@hotwired/stimulus";

/**
 * Reflects background-save progress. Listens on `window` for save events
 * (`{ detail: { state } }`) dispatched by `form-auto-save` controllers
 * anywhere on the page and toggles the saving / saved / error markup
 * accordingly. The channel defaults to `survey-save` (the builder header);
 * pass a matching `eventValue` to both controllers to use it elsewhere.
 *
 * `saved` is the resting state. `idle` (broadcast when a save is skipped)
 * falls back to that resting state so a stale "Saving…" never lingers.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["saving", "saved", "error"];
  static values = { event: { type: String, default: "survey-save" } };

  declare readonly eventValue: string;

  declare readonly savingTarget: HTMLElement;
  declare readonly hasSavingTarget: boolean;
  declare readonly savedTarget: HTMLElement;
  declare readonly hasSavedTarget: boolean;
  declare readonly errorTarget: HTMLElement;
  declare readonly hasErrorTarget: boolean;

  private boundOnSave = (event: Event) => this.onSave(event);

  connect() {
    window.addEventListener(this.eventValue, this.boundOnSave);
  }

  disconnect() {
    window.removeEventListener(this.eventValue, this.boundOnSave);
  }

  private onSave(event: Event) {
    const state = (event as CustomEvent).detail?.state ?? "saved";
    this.render(state === "saving" || state === "error" ? state : "saved");
  }

  private render(state: "saving" | "saved" | "error") {
    if (this.hasSavingTarget) this.savingTarget.classList.toggle("hidden", state !== "saving");
    if (this.hasSavedTarget) this.savedTarget.classList.toggle("hidden", state !== "saved");
    if (this.hasErrorTarget) this.errorTarget.classList.toggle("hidden", state !== "error");
  }
}
